import { memo, useEffect, useState } from 'react'
import { motion, AnimatePresence } from 'motion/react'
import { HomeCtas } from '@/components/ui/home/HomeCtas'
import { HomeMetric, HomeMetrics } from '@/components/ui/home/HomeMetrics'
import { fadeUpProps, usePrefersReducedMotion } from '@/lib/motion'

const ROTATING_LINES = [
  "Or Worth Knowing Why It Isn't.",
  'Or Your Next Expensive Mistake.',
  'Or Know if the market cares.',
]

type Props = {
  metrics: HomeMetric[]
}

/**
 * The rotating second line of the hero headline. Isolated into its own memoized
 * component so its 3s tick only re-renders this tiny subtree — not the whole
 * hero (metrics, CTAs, and the large blurred background layers), which used to
 * reconcile on every rotation and caused the swap to drop frames. The animated
 * span is promoted to its own GPU layer (translateZ + will-change) so the
 * opacity/transform crossfade stays composited at 60fps.
 */
const RotatingHeadline = memo(({ reduceMotion }: { reduceMotion: boolean }) => {
  const [lineIndex, setLineIndex] = useState(0)

  useEffect(() => {
    if (reduceMotion) return
    const timer = window.setInterval(() => {
      setLineIndex((i) => (i + 1) % ROTATING_LINES.length)
    }, 9000)
    return () => window.clearInterval(timer)
  }, [reduceMotion])

  if (reduceMotion) {
    return (
      <span className="block max-sm:font-[600] max-sm:whitespace-nowrap">
        {ROTATING_LINES[0]}
      </span>
    )
  }

  return (
    <AnimatePresence mode="wait">
      <motion.span
        key={lineIndex}
        className="block [transform:translateZ(0)] [will-change:transform,opacity] [backface-visibility:hidden] max-sm:font-[600] max-sm:whitespace-nowrap"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
      >
        {ROTATING_LINES[lineIndex]}
      </motion.span>
    </AnimatePresence>
  )
})

RotatingHeadline.displayName = 'RotatingHeadline'

/**
 * Full-viewport hero: fills exactly one screen below the sticky header (all breakpoints).
 * Copy, CTAs, and metrics are centered together as one block.
 */
export const HomeHeroSection = memo(({ metrics }: Props) => {
  const reduceMotion = usePrefersReducedMotion()
  const viewportShell = 'min-h-[calc(100dvh-var(--app-header-height))]'

  return (
    <section
      aria-label="Hero"
      className={`relative flex w-full flex-col overflow-hidden bg-[var(--background)] ${viewportShell}`}
    >
      <div className="pointer-events-none absolute inset-0" aria-hidden="true">
        <div className="absolute inset-0 bg-gradient-to-br from-[#F6CB86]/[0.05] via-transparent to-[#E0A263]/[0.06]" />
        <div className="absolute top-16 right-[8%] h-56 w-56 rounded-full bg-[#F6CB86]/10 blur-3xl sm:h-72 sm:w-72" />
        <div className="absolute bottom-8 left-[6%] h-64 w-64 rounded-full bg-[#E0A263]/10 blur-3xl sm:h-80 sm:w-80" />
        <div
          className="absolute inset-x-0 bottom-0 h-40 sm:h-48"
          style={{
            background:
              'radial-gradient(ellipse 90% 100% at 50% 100%, rgba(224, 162, 99, 0.08) 0%, transparent 72%)',
          }}
        />
      </div>

      <div
        className={`relative mx-auto flex w-full max-w-[1200px] min-w-0 flex-1 flex-col justify-center px-5 sm:px-6 lg:px-8 ${viewportShell}`}
      >
        <div className="mx-auto flex w-full max-w-[920px] min-w-0 flex-col items-center">
          <motion.div
            className="w-full text-center"
            {...fadeUpProps(reduceMotion)}
          >
            <div className="mb-6 inline-flex items-center rounded-full border border-[#F6CB86]/20 bg-[#F6CB86]/10 px-4 py-1.5 text-sm font-medium text-[#F6CB86]/70 sm:mb-4">
              Now Live · Join Vaagon
            </div>
            <h1 className="w-full font-sans text-white">
              <span className="block text-[clamp(24px,3.2vw,36px)] leading-[1.45] font-normal tracking-[0.5px] max-sm:text-[clamp(15px,4.15vw,24px)] max-sm:tracking-[0.2px] max-sm:whitespace-nowrap">
                Your Idea Is Either Worth Building
              </span>
              <span className="relative mx-auto mt-4 block h-[1.4em] overflow-hidden text-[clamp(28px,4.2vw,48px)] leading-[1.4] font-semibold text-white max-sm:max-w-none max-sm:text-[clamp(22px,5.4vw,30px)] max-sm:font-[600] max-sm:whitespace-nowrap sm:mt-3 sm:max-w-none">
                <RotatingHeadline reduceMotion={reduceMotion} />
              </span>
            </h1>

            <p className="mx-auto mt-7 max-w-[640px] text-center text-sm leading-relaxed text-[var(--foreground-muted)] sm:mt-6 sm:text-base lg:mt-8 dark:text-slate-300/80">
              Stop Building in the Dark. The Smartest Founders Don&apos;t Guess.
              They Validate.
            </p>

            <div className="mt-9 w-full sm:mt-8 lg:mt-10">
              <HomeCtas />
            </div>
          </motion.div>

          <motion.div
            className="w-full pt-8 sm:pt-6 lg:pt-10"
            {...fadeUpProps(reduceMotion, 0.15)}
          >
            <HomeMetrics metrics={metrics} />
          </motion.div>
        </div>
      </div>
    </section>
  )
})

HomeHeroSection.displayName = 'HomeHeroSection'
